import React, { useEffect, useRef, useState } from "react";
import { Mail, User, Phone, Globe, MessageSquare } from "lucide-react";
import Button from "../common/Button";
import emailjs from "emailjs-com";
import { useLocation } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

const ContactForm = () => {
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const recaptchaRef = useRef(null);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setCaptchaError("");
  };

  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 250,
      behavior: "smooth",
    });
  }, [location.pathname]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    website: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!captchaValue) {
      setCaptchaError("Please verify that you are not a robot.");
      return;
    }

    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name || "");
      formDataToSend.append("email", formData.email || "");
      formDataToSend.append("mobile", formData.phone || "");
      formDataToSend.append("message", formData.message || "");
      formDataToSend.append("websiteurl", formData.website || "NA");

      const response = await fetch(
        "https://tissa-api.tissatech.in/contactus_sendmail.php",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      const result = await response.json();

      if (result.status === "ok") {
        setSubmitting(false);
        setSubmitStatus({
          type: "success",
          message:
            "Your message has been sent successfully. We will get back to you soon!",
        });

        setFormData({
          name: "",
          email: "",
          phone: "",
          website: "",
          message: "",
        });

        recaptchaRef.current.reset();
        setCaptchaValue(null);

        setTimeout(() => setSubmitStatus({ type: "", message: "" }), 7000);
      } else {
        setSubmitting(false);
        setSubmitStatus({
          type: "error",
          message: "Failed to send the message. Please try again later.",
        });
      }
    } catch (error) {
      console.log("Error sending mail:", error);
      setSubmitting(false);
      setSubmitStatus({
        type: "error",
        message: "Something went wrong. Please try again later.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          <User size={18} />
        </div>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Your Full Name *"
          className={`block w-full pl-10 pr-4 py-3 border ${
            errors.name ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        {errors.name && (
          <p className="text-red-500 text-sm mt-1">{errors.name}</p>
        )}
      </div>

      {/* Email Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          <Mail size={18} />
        </div>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Your Email *"
          className={`block w-full pl-10 pr-4 py-3 border ${
            errors.email ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </div>

      {/* Phone Field */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          <Phone size={18} />
        </div>
        <input
          type="text"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          placeholder="Your Phone *"
          className={`block w-full pl-10 pr-4 py-3 border ${
            errors.phone ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />
        {errors.phone && (
          <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
        )}
      </div>

      {/* Website Field (Optional) */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
          <Globe size={18} />
        </div>
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          placeholder="Your Website"
          className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Message Field */}
      <div className="relative">
        <div className="absolute top-3 left-3 text-gray-400">
          <MessageSquare size={18} />
        </div>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Write Message *"
          rows="4"
          className={`block w-full pl-10 pr-4 py-3 border ${
            errors.message ? "border-red-500" : "border-gray-300"
          } rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
        ></textarea>
        {errors.message && (
          <p className="text-red-500 text-sm mt-1">{errors.message}</p>
        )}
      </div>

      <div className="mt-4 flex">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey="6LeNuOMrAAAAAEy9XUUXRP9K2D7xH7hOxAeycx5H"
          onChange={handleCaptchaChange}
        />
      </div>
      {captchaError && <p className="text-red-500 text-sm">{captchaError}</p>}

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="w-full"
        disabled={submitting}
      >
        {submitting ? (
          <div className="flex items-center justify-center">
            <svg
              className="animate-spin h-5 w-5 mr-2 text-white"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.37 0 0 5.37 0 12h4z"
              />
            </svg>
            Processing...
          </div>
        ) : (
          "Send Message"
        )}
      </Button>

      {/* Status Message */}
      {submitStatus.message && (
        <div
          className={`mt-4 p-3 rounded ${
            submitStatus.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {submitStatus.message}
        </div>
      )}
    </form>
  );
};

export default ContactForm;
